import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

import { useImportCardPool } from '../../hooks';
import { formatPoints } from '../../lib/format';
import type { CardPoolOperationResult } from '../../model/types';

interface ImportPoolDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Import a free-card pool from an Excel/CSV file (gated by `loyalty.manage`). */
export function ImportPoolDialog({ open, onClose, onDone }: ImportPoolDialogProps) {
  const { t } = useTranslation('loyalty');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CardPoolOperationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const importPool = useImportCardPool();

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const submit = () => {
    if (!file) return;
    setError(null);
    importPool.mutate(file, {
      onSuccess: (res) => {
        setResult(res);
        onDone(t('importPoolDialog.result', { added: formatPoints(res.added), received: formatPoints(res.received) }));
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('importPoolDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>{t('importPoolDialog.description')}</DialogContentText>
          <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
            {file ? file.name : t('importPoolDialog.chooseFile')}
            <input
              hidden
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {result ? (
            <Alert severity="success">
              {t('importPoolDialog.resultSummary', {
                received: formatPoints(result.received),
                added: formatPoints(result.added),
                duplicates: formatPoints(result.duplicates),
                invalid: formatPoints(result.invalid),
              })}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={importPool.isPending}>
          {result ? t('common:action.close') : t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!file || importPool.isPending}
          startIcon={importPool.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.import')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
